import { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  AddressAutocomplete,
  Alert,
  Button,
  ErrorSummary,
  Input,
  Select,
  postOrderLog,
  usePaymentMessages,
  useCart,
  verifyAddress,
  type AddressFields,
  type ErrorSummaryItem,
  type PaymentTitle,
} from '@alwindows/shared'
import { postSpareOrder, type SpareOrderPayload } from '@alwindows/shared/api/spareParts'
import { useShipment } from '../shipment/ShipmentContext'

type FormState = {
  name: string
  email: string
  confirmEmail: string
  telNumber: string
  address: AddressFields | null
  manualAddress: boolean
  street: string
  city: string
  state: string
  postcode: string
  agree: boolean
}

const INITIAL: FormState = {
  name: '',
  email: '',
  confirmEmail: '',
  telNumber: '',
  address: null,
  manualAddress: false,
  street: '',
  city: '',
  state: '',
  postcode: '',
  agree: false,
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// State code is what the SAP order payload expects (matches CollectOption.State).
// Full names are shown to the user so it's clear which territory they're picking.
const AU_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'SA', name: 'South Australia' },
  { code: 'WA', name: 'Western Australia' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'NT', name: 'Northern Territory' },
]

// Field-level error identifiers. Each maps to a single rendered control or
// (for `address`) a section. Keeps the summary list and the focus-on-error
// jump table in sync.
type FieldKey = 'name' | 'email' | 'confirmEmail' | 'telNumber' | 'address' | 'agree'

const FIELD_IDS: Record<FieldKey, string> = {
  name: 'checkout-name',
  email: 'checkout-email',
  confirmEmail: 'checkout-confirm-email',
  telNumber: 'checkout-tel',
  address: 'checkout-address',
  agree: 'checkout-agree',
}

const FIELD_LABELS: Record<FieldKey, string> = {
  name: 'Name',
  email: 'Email',
  confirmEmail: 'Confirm email',
  telNumber: 'Phone',
  address: 'Address',
  agree: 'Terms and conditions',
}

// Which `FieldKey` to clear when a given form field is edited. Multiple
// address sub-fields all clear the single `address` error.
const ERROR_FOR_FIELD: Partial<Record<keyof FormState, FieldKey>> = {
  name: 'name',
  email: 'email',
  confirmEmail: 'confirmEmail',
  telNumber: 'telNumber',
  street: 'address',
  city: 'address',
  state: 'address',
  postcode: 'address',
  agree: 'agree',
}

export function Checkout() {
  const { cart, clear } = useCart()
  const { selection } = useShipment()
  const navigate = useNavigate()

  const [form, setForm] = useState<FormState>(INITIAL)
  const [submitting, setSubmitting] = useState(false)
  // Field-level validation errors. Cleared per-key as the user edits.
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
  // Server / post-validation errors that don't belong to a single field
  // (e.g. network failures, lost shipment selection).
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Focused on submit-failure so screen readers announce the full error list.
  const errorSummaryRef = useRef<HTMLDivElement>(null)
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null)
  const [paymentTitle, setPaymentTitle] = useState<PaymentTitle>('Payment')
  // The payment-title transition is the user's primary signal that a charge
  // succeeded or failed. Move focus to the heading so AT users get the
  // change announced via the focus event in addition to the aria-live region.
  const paymentTitleRef = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (paymentUrl && paymentTitle !== 'Payment') {
      paymentTitleRef.current?.focus()
      if (typeof document !== 'undefined') document.title = paymentTitle
    }
  }, [paymentTitle, paymentUrl])

  // Guard: if the user lands here without a shipment selection or with an
  // empty cart, bounce them back — but only while the form is showing. Once
  // the order is placed, the gateway's AL_ECOM_CLEAR_CART empties the cart on
  // purpose and the customer must stay on the payment view to see the
  // receipt and the "Thank you" state (legacy called resetCart() and stayed).
  useEffect(() => {
    if (paymentUrl) return
    if (!selection || cart.items.length === 0) {
      navigate('/cart', { replace: true })
    }
  }, [selection, cart.items.length, navigate, paymentUrl])

  // Pickup pre-fills the depot address into the form.
  useEffect(() => {
    if (selection?.method === 'Pickup') {
      const o = selection.option
      setForm((f) => ({
        ...f,
        street: o.Street,
        city: o.Suburb,
        state: o.State,
        postcode: o.Postcode,
      }))
    }
  }, [selection])

  const isPickup = selection?.method === 'Pickup'

  usePaymentMessages(paymentUrl, {
    onClearCart: () => clear(),
    onTitleChange: (t: PaymentTitle) => setPaymentTitle(t),
  })

  const clearError = (key: FieldKey) => {
    setErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    const errKey = ERROR_FOR_FIELD[key]
    if (errKey) clearError(errKey)
  }

  const handleAddressChange = (fields: AddressFields) => {
    setForm((prev) => ({
      ...prev,
      address: fields,
      street: fields.street,
      city: fields.city,
      state: fields.state,
      postcode: fields.postcode,
    }))
    clearError('address')
  }

  // Inline checkers that mirror the submit-time rules. Surfaced on blur so
  // the user sees a typo (`jane@example,com`) before they reach the bottom
  // of the form, and again on submit as the safety net.
  const handleEmailBlur = () => {
    if (!form.email) {
      setErrors((prev) => ({ ...prev, email: 'Enter your email address' }))
    } else if (!EMAIL_RE.test(form.email)) {
      setErrors((prev) => ({ ...prev, email: 'Enter a valid email address' }))
    }
  }

  const handleConfirmEmailBlur = () => {
    if (!form.confirmEmail) {
      setErrors((prev) => ({ ...prev, confirmEmail: 'Confirm your email address' }))
    } else if (form.email && form.email !== form.confirmEmail) {
      setErrors((prev) => ({ ...prev, confirmEmail: 'Emails do not match' }))
    }
  }

  // Collect every validation failure in one pass so the summary lists them
  // all together rather than the user fixing one only to hit the next.
  const validate = (): Partial<Record<FieldKey, string>> => {
    const e: Partial<Record<FieldKey, string>> = {}
    if (!form.name) e.name = 'Enter your name'
    if (!form.email) e.email = 'Enter your email address'
    else if (!EMAIL_RE.test(form.email)) e.email = 'Enter a valid email address'
    if (!form.confirmEmail) e.confirmEmail = 'Confirm your email address'
    else if (form.email && form.email !== form.confirmEmail) e.confirmEmail = 'Emails do not match'
    if (!form.telNumber) e.telNumber = 'Enter your phone number'
    // Skip address validation for Pickup — the depot address is supplied by
    // the selection, not the user, and the address inputs aren't rendered.
    if (!isPickup && (!form.street || !form.city || !form.state || !form.postcode)) {
      e.address = 'Provide a complete delivery address'
    }
    if (!form.agree) e.agree = 'You must agree to the terms and conditions'
    return e
  }

  const focusErrorSummary = () => {
    // rAF so the summary has mounted before we move focus to it.
    requestAnimationFrame(() => errorSummaryRef.current?.focus())
  }

  const submit = async () => {
    if (submitting) return
    setSubmitError(null)

    const validationErrors = validate()
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      focusErrorSummary()
      return
    }

    setSubmitting(true)
    try {
      if (!isPickup) {
        const verified = await verifyAddress(
          `${form.street} ${form.city} ${form.state} ${form.postcode}`,
        )
        if (!verified) {
          setErrors({ address: 'Enter a valid Australian address.' })
          focusErrorSummary()
          setSubmitting(false)
          return
        }
      }

      if (!selection) {
        setSubmitError('Lost the shipment selection. Please return to the cart.')
        setSubmitting(false)
        return
      }

      const shipmentValue =
        selection.method === 'Pickup'
          ? selection.option.OptNo
          : selection.packagingOption.Description

      const payload: SpareOrderPayload = {
        Name: form.name,
        Email: form.email,
        TelNumber: form.telNumber,
        Street: form.street,
        PostCode: form.postcode,
        City: form.city,
        State: form.state,
        ShipmentType: selection.method,
        ShipmentValue: shipmentValue,
        ToItm: cart.items.map((item) => ({ MaterialNo: item.sku, Qty: item.qty })),
      }

      const response = await postSpareOrder(payload)

      // Best-effort log to WP. Fire and forget; never block the user.
      void postOrderLog({
        app: 'spare_parts',
        order_number: response.RefNo,
        line_items: cart.items.map((i) => ({
          sku: i.sku,
          qty: i.qty,
          price: i.price,
          name: i.name,
        })),
        total: cart.items.reduce((s, i) => s + i.qty * i.price, 0),
      })

      window.scrollTo(0, 0)
      setPaymentUrl(response.paymentURL)
      setPaymentTitle('Payment')
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Checkout failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (paymentUrl) {
    return (
      <div>
        <h2
          className="al-checkout__title"
          ref={paymentTitleRef}
          tabIndex={-1}
          aria-live="polite"
          aria-atomic="true"
        >
          {paymentTitle}
        </h2>
        <iframe
          className="al-checkout__iframe"
          title="Payment"
          src={paymentUrl}
          scrolling="no"
          frameBorder={0}
        />
        {paymentTitle === 'Thank you' ? (
          <Alert variant="success" title="Order placed">
            Your order has been received. You&apos;ll get a confirmation email shortly.
          </Alert>
        ) : null}
        {paymentTitle === 'Sorry' ? (
          <Alert variant="error" title="Payment unsuccessful">
            Something went wrong with the payment. Please return to the cart and try again.
          </Alert>
        ) : null}
      </div>
    )
  }

  return (
    <div>
      <Link to="/cart" className="al-cart__back m-bx4">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Shopping Cart
      </Link>
      <h2>Checkout</h2>

      <ErrorSummary
        ref={errorSummaryRef}
        items={(Object.keys(errors) as FieldKey[]).map<ErrorSummaryItem>((k) => ({
          key: k,
          label: FIELD_LABELS[k],
          message: errors[k]!,
          targetId: FIELD_IDS[k],
        }))}
      />

      <section>
        <h3>Contact details</h3>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.name}
              label="Name"
              required
              error={errors.name}
              value={form.name}
              maxLength={70}
              autoComplete="name"
              onChange={(e) => update('name', e.target.value)}
              className="input-text"
            />
          </div>
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.telNumber}
              label="Phone"
              required
              error={errors.telNumber}
              type="tel"
              value={form.telNumber}
              maxLength={30}
              autoComplete="tel"
              onChange={(e) => update('telNumber', e.target.value)}
              className="input-text"
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.email}
              label="Email"
              required
              error={errors.email}
              type="email"
              value={form.email}
              maxLength={241}
              autoComplete="email"
              onChange={(e) => update('email', e.target.value)}
              onBlur={handleEmailBlur}
              className="input-text"
            />
          </div>
          <div className="col-xs-12 col-md-6">
            <Input
              id={FIELD_IDS.confirmEmail}
              label="Confirm email"
              required
              error={errors.confirmEmail}
              type="email"
              value={form.confirmEmail}
              maxLength={241}
              autoComplete="email"
              onChange={(e) => update('confirmEmail', e.target.value)}
              onBlur={handleConfirmEmailBlur}
              className="input-text"
            />
          </div>
        </div>
      </section>

      {!isPickup ? (
        <section id={FIELD_IDS.address} tabIndex={-1} className="m-bx5">
          <h3>Address</h3>
          {form.manualAddress ? (
            <div className="al-checkout__manual">
              <Input
                label="Street"
                required
                value={form.street}
                autoComplete="street-address"
                onChange={(e) => update('street', e.target.value)}
                className="input-text"
              />
              <Input
                label="City or Suburb"
                required
                value={form.city}
                autoComplete="address-level2"
                onChange={(e) => update('city', e.target.value)}
                className="input-text"
              />
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-4">
                  <Input
                    label="Postcode"
                    required
                    value={form.postcode}
                    autoComplete="postal-code"
                    onChange={(e) =>
                      update('postcode', e.target.value.replace(/\D/g, '').slice(0, 4))
                    }
                    className="input-text"
                  />
                </div>
                <div className="col-xs-12 col-sm-6 col-md-4 dropdown">
                  <Select
                    label="State"
                    required
                    value={form.state}
                    autoComplete="address-level1"
                    onChange={(e) => update('state', e.target.value)}
                  >
                    <option value="">Select a state</option>
                    {AU_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          ) : (
            <>
              <AddressAutocomplete
                hideLabel
                onChange={handleAddressChange}
                onModeChange={(mode) => update('manualAddress', mode === 'manual')}
              />
              {form.address ? (
                <p className="al-checkout__addr-summary">
                  Delivering to:{' '}
                  <strong>
                    {form.street}, {form.city} {form.state} {form.postcode}
                  </strong>
                </p>
              ) : null}
            </>
          )}
          {errors.address ? (
            <p role="alert" className="al-field__error">
              {errors.address}
            </p>
          ) : null}
        </section>
      ) : null}

      <section>
        <input
          id={FIELD_IDS.agree}
          type="checkbox"
          className="input-checkbox"
          checked={form.agree}
          aria-invalid={errors.agree ? true : undefined}
          aria-describedby={errors.agree ? `${FIELD_IDS.agree}-err` : undefined}
          onChange={(e) => update('agree', e.target.checked)}
        />
        <label htmlFor={FIELD_IDS.agree} className="al-checkout__agree">
          I have read and agree to A&amp;L&rsquo;s{' '}
          <a
            href="https://www.alwindows.com.au/terms-and-conditions/"
            target="_blank"
            rel="noopener noreferrer"
            className="al-checkout__terms-link"
          >
            terms and conditions of sale
          </a>
        </label>
        {errors.agree ? (
          <p id={`${FIELD_IDS.agree}-err`} role="alert" className="al-field__error">
            {errors.agree}
          </p>
        ) : null}
      </section>

      {submitError ? <Alert variant="error">{submitError}</Alert> : null}

      <div className="al-checkout__actions m-tx6">
        <Button variant="outline" asChild>
          <Link to="/cart">Back to cart</Link>
        </Button>
        <Button variant="primary-outline" fill onClick={submit} disabled={submitting}>
          {submitting ? 'Processing…' : 'Place order'}
        </Button>
      </div>
    </div>
  )
}
